import React, { useEffect, useState, useCallback, useRef } from "react";
import { Empty, Skeleton } from "antd";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import AdminHOC from "../../../../components/shared/HOC/AdminHOC.jsx";
import { DownloadOutlined } from "@ant-design/icons";
import LottieIcon from "../../../../components/shared/lottieIcon/LottieIcon.jsx";
import searchIconsvg from "../../../../assets/icons/Search-Icon.svg";
import CommonTable from "../../../../components/shared/table/Table.jsx";
import backButton from "../../../../assets/back-btn.svg";
import CustomLabelDropdown from "../../../../components/shared/new Form/Fields/CustomLabelDropdown.jsx";
import CustomButton from "../../../../components/global/Button/CustomButton.jsx";
import CustomInput from "../../../../components/global/CustomInput/CustomInput.jsx";
import { getDynamicGroupColumnsReport, LEARNER_COLUMNS,BUNDLE_COURSE_COLUMNS } from "../../../../assets/FormFeilds/GroupSingleReportColumns.js";
import {
  FILTER_TYPE_OPTIONS,
  STATUS_FILTER_OPTIONS,
} from "../../../../assets/constants/admin/groupSingleReportFilter.js";
import { downloadSingleGroupReport } from "../../../../service/AdminService/groupService.js";
import { useGroupReport } from "../../../../hooks/GroupHooks/useSingleGroupReport.js";
import usePagination from "../../../../assets/hooks/PaginationHooks/usePagination.jsx";
import { showErrorMessage, showSuccessMessage } from "../../../../components/shared/pagination/messages.js";

const DEFAULT_FILTERS = {
  subType: null,
  courseId: null,
  bundleId: null,
  status: null,
};

const LearnerTable = ({ learners }) => {
  if (!learners?.length) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  return (
    <CommonTable
      data={learners}
      columns={LEARNER_COLUMNS}
      pagination={false}
      serverSide={false}
      rowKey="userId"
    />
  );
};

const SingleGroupReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { groupId } = useParams();
  const tableRef = useRef(null);

  const {
    data,
    totalRecords,
    filterOptions,
    loading,
    filterOptionsLoading,
    fetchFilterOptions,
    fetchReport,
  } = useGroupReport();

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    setTotalElements,
  } = usePagination();

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [searchText, setSearchText] = useState("");
  const [expandedRowKey, setExpandedRowKey] = useState(null);
  const [isSearchHovered, setIsSearchHovered] = useState(false);
  const [pageLoader, setPageLoader] = useState(false);

  const groupName = filterOptions.groupDetails?.name;

  const courseOptions = filterOptions.courseNames.map(({ id, name }) => ({
    value: id,
    label: name,
  }));

  const bundleOptions = filterOptions.bundleNames.map(({ id, name }) => ({
    value: id,
    label: name,
  }));

  const updateFilters = useCallback((updates) => {
    setFilters(prev => ({ ...prev, ...updates }));
    setCurrentPage(1);
  }, [setCurrentPage]);

  const handleSubTypeChange = useCallback((value) => {
    updateFilters({ subType: value ?? null, courseId: null, bundleId: null });
  }, [updateFilters]);

  useEffect(() => {
    fetchFilterOptions(groupId);
  }, [groupId, fetchFilterOptions]);

  const fetchGroupReport = useCallback(async () => {
    try {
      const response = await fetchReport(groupId, {
        page: currentPage - 1,
        size: pageSize,
        search: searchText.trim() || undefined,
        subType: filters.subType || undefined,
        courseId: filters.courseId || undefined,
        bundleId: filters.bundleId || undefined,
        status: filters.status || undefined,
        sortDir: "DESC",
      });
      if (response?.status === "SUCCESS") {
        setTotalElements(response.data?.totalRecords || 0);
      }
    } catch (error) {
      showErrorMessage("Failed to Load Data");
    }
  }, [groupId, currentPage, pageSize, searchText, filters, fetchReport, setTotalElements]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchGroupReport();
    }, 500);
    return () => clearTimeout(handler);
  }, [fetchGroupReport]);

  const handleDownload = async () => {
    setPageLoader(true);
    try {
      await downloadSingleGroupReport(groupId);
      showSuccessMessage("Report Downloaded Successfully!");
    } catch (err) {
      showErrorMessage("Failed to download report");
    } finally {
      setPageLoader(false);
    }
  };

  const handleSearch = useCallback((query) => {
    setSearchText(query);
    setCurrentPage(1);
  }, [setCurrentPage]);

  const handleTableChange = useCallback((pagination) => {
    if (!pagination) return;
    if (pagination.pageSize !== pageSize) {
      setPageSize(pagination.pageSize);
      setCurrentPage(1);
    } else {
      setCurrentPage(pagination.current);
    }
    const scrollable = tableRef.current?.querySelector(".ant-table-body");
    if (scrollable) scrollable.scrollTo({ top: 0, behavior: "auto" });
  }, [pageSize, setPageSize, setCurrentPage]);

  const renderExpandedRow = useCallback((record) => {
    if (record.bundleId) {
      return (
        <CommonTable
          data={record.courses || []}
          columns={BUNDLE_COURSE_COLUMNS}
          pagination={false}
          serverSide={false}
          rowKey="courseId"
          expandedRowRender={(courseRecord) => (
            <LearnerTable learners={courseRecord.learners} />
          )}
          rowExpandable={(courseRecord) => !!courseRecord.learners?.length}
        />
      );
    }
    return <LearnerTable learners={record.learners} />;
  }, []);

  return (
    <div className="p-6 flex flex-col border border-[#E8E8E8] rounded-[10px] shadow-[0px_0px_6px_0px_rgba(0,0,0,0.05)] gap-4">
      <div className="flex flex-col">
        <div className="flex justify-between items-center">
          <h1 className="text-[#e74860] text-[1.5rem] font-normal">
            {filterOptionsLoading ? (
              <Skeleton.Input active style={{ width: 220, height: 28, borderRadius: 6 }} />
            ) : (
              `${groupName} Report`
            )}
          </h1>
          <div
            className="flex gap-2 justify-center cursor-pointer"
            onClick={() => navigate(-1)}
          >
            <img alt="back-btn" src={backButton} />
            <span>Back</span>
          </div>
        </div>

        <div className="flex gap-4 mt-4 flex-wrap">
          <div
            className="w-[260px] shrink-0"
            onMouseEnter={() => setIsSearchHovered(true)}
            onMouseLeave={() => setIsSearchHovered(false)}
          >
            <CustomInput
              icon={
                isSearchHovered
                  ? <LottieIcon name="searchIcon" size={20} shouldPlay />
                  : <img src={searchIconsvg} alt="search" className="w-5 h-5" />
              }
              className="w-full"
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search courses or curriculums"
              allowClear
            />
          </div>
          <div className="flex-1">
            <CustomLabelDropdown
              value={filters.subType}
              onChange={handleSubTypeChange}
              options={FILTER_TYPE_OPTIONS}
              placeholder="All Types"
              wrapperClass="w-full"
              allowClear
            />
          </div>

          {filters.subType === "COURSE" && (
            <div className="flex-1">
              <CustomLabelDropdown
                value={filters.courseId}
                onChange={(v) => updateFilters({ courseId: v ?? null })}
                options={courseOptions}
                placeholder="All Courses"
                wrapperClass="w-full"
                loading={filterOptionsLoading}
                allowClear
                showSearch
              />
            </div>
          )}

          {filters.subType === "BUNDLE" && (
            <div className="flex-1">
              <CustomLabelDropdown
                value={filters.bundleId}
                onChange={(v) => updateFilters({ bundleId: v ?? null })}
                options={bundleOptions}
                placeholder="All Curriculums"
                wrapperClass="w-full"
                loading={filterOptionsLoading}
                allowClear
                showSearch
              />
            </div>
          )}

          <div className="flex-1">
            <CustomLabelDropdown
              value={filters.status}
              onChange={(v) => updateFilters({ status: v ?? null })}
              options={STATUS_FILTER_OPTIONS}
              placeholder="All Status"
              wrapperClass="w-full"
              allowClear
            />
          </div>

          <div className="shrink-0">
            <CustomButton
              icon={<DownloadOutlined />}
              onClick={handleDownload}
              variant="primary"
            >
              Export
            </CustomButton>
          </div>
        </div>
      </div>

      <div ref={tableRef}>
        <CommonTable
          data={data}
          columns={getDynamicGroupColumnsReport({ subType: filters.subType })}
          serverSide
          showLoader={loading}
          rowKey={(record) => record.courseId ?? record.bundleId}
          scroll={{ y: "calc(100vh - 420px)" }}
          pagination={{
            current: currentPage,
            pageSize,
            total: totalRecords,
          }}
          onChange={handleTableChange}
          expandedRowRender={renderExpandedRow}
          rowExpandable={(record) => !!(record.learners?.length || record.courses?.length)}
        />
      </div>
    </div>
  );
};

export default AdminHOC(SingleGroupReport);